import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

import { usePlanningsDeliveriesCache, usePlanningsDriversCache, usePlanningsTrucksCache } from './usePlanningsCache';

const requestInfiniteCollectionPath = (startOn: string, endOn: string, page: number, perPage: number) => `/api/v3/plannings/${startOn}/deliveries?end_on=${endOn}&page=${page}&per_page=${perPage}`;
const requestCollectionPath = (startOn: string, endOn: string) => `/api/v3/plannings/${startOn}/deliveries?end_on=${endOn}`;
const requestMemberPath = (id: number) => `/api/v3/plannings/deliveries/${id}`;

const getInfiniteEntities = (startOn: string, endOn: string, perPage: number, queryClient: QueryClient) => async ({ pageParam = 1, queryKey }) => {
  const requestPath = requestInfiniteCollectionPath(startOn, endOn, pageParam, perPage);
  const response: AxiosResponse<{
    ids: number[],
    total_pages_count: number;
    data: PlanningsDeliveryEntity[];
  }> = await axios.get(requestPath);
  return response.data;
};

const getEntities = (startOn: string, endOn: string, queryClient: QueryClient) => async () => {
  const response: AxiosResponse<PlanningsDeliveryEntity[]> = await axios.get(requestCollectionPath(startOn, endOn));

  return response.data.map((entity) => {
    queryClient.setQueryData(['useQueryPlanningsDelivery', { id: entity.id }], entity);

    return entity;
  });
};

const getEntity = (id: number) => async () => {
  const response: AxiosResponse<PlanningsDeliveryEntity> = await axios.get(requestMemberPath(id));

  return response.data;
};

export const USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY = 'useQueryPlanningsDeliveries';
export const useInfiniteQueryPlanningsDeliveries = (startOn: string, endOn: string, perPage: number) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: [USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY, startOn, endOn, perPage],
    queryFn: getInfiniteEntities(startOn, endOn, perPage, queryClient),
    initialPageParam: 1,
    staleTime: Infinity,
    enabled: Boolean(startOn) && Boolean(endOn),
    getNextPageParam: (lastPage, allPages) => (allPages.length < lastPage.total_pages_count ? allPages.length + 1 : undefined),
  });
};

/**
 * fixedRouteId は固定ルートで絞り込むカード配車だけが渡す。/map・V3Plans・V3Timeline は null。
 * 付けるかどうかで返る一覧が変わるので、キャッシュキーにも含める。
 */
export const buildDeliveriesRequestPath = (
  startOn: string,
  endOn: string,
  page: number,
  perPage: number,
  fixedRouteId: number | null,
) => {
  const base = `/api/v5/plannings/${startOn}/deliveries?end_on=${endOn}&page=${page}&per_page=${perPage}`;
  return fixedRouteId == null ? base : `${base}&fixed_route_id=${fixedRouteId}`;
};

export const PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY = 'useInfiniteQueryPlanningsDeliveriesWithLegRuns';

export const buildDeliveriesQueryKey = (
  startOn: string,
  endOn: string,
  perPage: number,
  fixedRouteId: number | null,
) => [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY, startOn, endOn, perPage, fixedRouteId] as const;

const getInfiniteDeliveriesWithLegRunsEntities = (
  startOn: string,
  endOn: string,
  perPage: number,
  fixedRouteId: number | null,
) => async ({ pageParam = 1 }: { pageParam?: number }) => {
  const requestPath = buildDeliveriesRequestPath(startOn, endOn, pageParam, perPage, fixedRouteId);
  const response: AxiosResponse<{
    ids: number[],
    total_pages_count: number;
    data: PlanningsDeliveryWithLegRunEntity[];
  }> = await axios.get(requestPath);
  return response.data;
};

/**
 * delivery 一覧の唯一の取得口。/map・カード配車・V3Plans・V3Timeline が共有する。
 *
 * 以前はカード配車系だけ別フック（useConvenienceDispatchDeliveriesQuery）を持っており、
 * 同じ API を叩きながらキャッシュキーが分かれていた。片方の画面で配車しても
 * もう片方の invalidate が届かないため、1 つに寄せている。
 */
export const useInfiniteQueryPlanningsDeliveriesWithLegRuns = (
  startOn: string,
  endOn: string,
  perPage: number,
  fixedRouteId: number | null = null,
) => useInfiniteQuery({
  queryKey: buildDeliveriesQueryKey(startOn, endOn, perPage, fixedRouteId),
  queryFn: getInfiniteDeliveriesWithLegRunsEntities(startOn, endOn, perPage, fixedRouteId),
  initialPageParam: 1,
  staleTime: Infinity,
  enabled: Boolean(startOn) && Boolean(endOn),
  getNextPageParam: (lastPage, allPages) => (allPages.length < lastPage.total_pages_count ? allPages.length + 1 : undefined),
});

export const useQueryPlanningsDeliveries = (startOn: string, endOn: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY, { startOn, endOn }],
    queryFn: getEntities(startOn, endOn, queryClient),
    staleTime: Infinity,
    enabled: Boolean(startOn) && Boolean(endOn)
  });
};

export const useQueryPlanningsDelivery = (id: number) => useQuery({
  queryKey: ['useQueryPlanningsDelivery', { id }],
  queryFn: getEntity(id),
  staleTime: Infinity,
  enabled: Boolean(id)
});

export const useMutationPlanningDelivery = () => {
  const deliveriesCache = usePlanningsDeliveriesCache();
  const trucksCache = usePlanningsTrucksCache();
  const driversCache = usePlanningsDriversCache();

  return {
    delete: useMutation({
      mutationFn: (deliveryToDelete: PlanningsDeliveryEntity) => axios.delete(requestMemberPath(deliveryToDelete.id)),

      onSuccess: (_data, variables) => {
        deliveriesCache.removeDeliveryById(variables.id);
        trucksCache.removeTruckById(variables.truckId);
        driversCache.removeDriverById(variables.driverId);
      }
    }),
  };
};
